export const IOS_APP_STORE_URL =
  "https://apps.apple.com/us/app/solmi-ai/id6759606508";
export const IOS_APP_STORE_NATIVE_URL =
  "itms-apps://itunes.apple.com/app/id6759606508";
export const ANDROID_PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.aisongmaker.app";
export const SOLMI_APP_DEEP_LINK_URL = "aisongmaker://";

export type AppDownloadAttribution = {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
  clickId?: string;
};

const DEFAULT_ATTRIBUTION: Required<
  Pick<AppDownloadAttribution, "source" | "medium" | "campaign" | "content">
> = {
  source: "solmi_website",
  medium: "website",
  campaign: "app_download",
  content: "website_download",
};

export const SMART_BANNER_ATTRIBUTION: AppDownloadAttribution = {
  source: "solmi_website",
  medium: "smart_banner",
  campaign: "app_download",
  content: "smart_banner",
};

export const MOBILE_PROMPT_ATTRIBUTION: AppDownloadAttribution = {
  source: "solmi_website",
  medium: "mobile_prompt",
  campaign: "app_download",
  content: "mobile_prompt",
};

export const DOWNLOAD_PAGE_ATTRIBUTION: AppDownloadAttribution = {
  source: "solmi_website",
  medium: "download_page",
  campaign: "app_download",
  content: "download_page",
};

export const LANDING_ATTRIBUTION: AppDownloadAttribution = {
  source: "solmi_website",
  medium: "landing_page",
  campaign: "app_download",
  content: "landing_app_section",
};

export const QR_DOWNLOAD_ATTRIBUTION: AppDownloadAttribution = {
  source: "solmi_website",
  medium: "qr",
  campaign: "app_download",
  content: "qr_code",
};

export const APP_DOWNLOAD_URL = "https://solmi.ai/download";
export const APP_DOWNLOAD_QR_IMAGE = "/mobile/solmi-download-qr.svg";

export type AppDownloadPlatform = "ios" | "android" | "other";

function normalizeAttribution(
  attribution: AppDownloadAttribution = {},
): Required<
  Pick<AppDownloadAttribution, "source" | "medium" | "campaign" | "content">
> &
  Pick<AppDownloadAttribution, "term" | "clickId"> {
  return {
    ...DEFAULT_ATTRIBUTION,
    ...attribution,
  };
}

function appendParams(baseUrl: string, params: Record<string, string>): string {
  const url = new URL(baseUrl);
  for (const [key, value] of Object.entries(params)) {
    url.searchParams.set(key, value);
  }
  return url.toString();
}

function buildReferrer(attribution: AppDownloadAttribution = {}): string {
  const normalized = normalizeAttribution(attribution);
  const params = new URLSearchParams({
    utm_source: normalized.source,
    utm_medium: normalized.medium,
    utm_campaign: normalized.campaign,
    utm_content: normalized.content,
  });

  if (normalized.term) params.set("utm_term", normalized.term);
  if (normalized.clickId) params.set("web_click_id", normalized.clickId);

  return params.toString();
}

function buildAppleCampaignToken(
  attribution: AppDownloadAttribution = {},
): string {
  const normalized = normalizeAttribution(attribution);
  return [normalized.campaign, normalized.content]
    .filter(Boolean)
    .join("_")
    .replace(/[^a-zA-Z0-9_.-]/g, "_")
    .slice(0, 40);
}

export function createAppDownloadClickId(source = "web"): string {
  const safeSource = source.replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 12);
  const uuid =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID().replace(/-/g, "").slice(0, 20)
      : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  return `${safeSource}_${uuid.replace(/[^a-zA-Z0-9_-]/g, "")}`.slice(0, 36);
}

export function getIosAppStoreUrl(
  attribution: AppDownloadAttribution = {},
): string {
  return appendParams(IOS_APP_STORE_URL, {
    ct: buildAppleCampaignToken(attribution),
    mt: "8",
  });
}

export function getIosNativeAppStoreUrl(
  attribution: AppDownloadAttribution = {},
): string {
  return appendParams(IOS_APP_STORE_NATIVE_URL, {
    ct: buildAppleCampaignToken(attribution),
    mt: "8",
  });
}

export function getAndroidPlayStoreUrl(
  attribution: AppDownloadAttribution = {},
): string {
  return appendParams(ANDROID_PLAY_STORE_URL, {
    referrer: buildReferrer(attribution),
  });
}

export function getAndroidAppIntentUrl(
  attribution: AppDownloadAttribution = {},
): string {
  return `intent://open#Intent;scheme=aisongmaker;package=com.aisongmaker.app;S.browser_fallback_url=${encodeURIComponent(
    getAndroidPlayStoreUrl(attribution),
  )};end`;
}

export function getAppDownloadTrackingParams(
  attribution: AppDownloadAttribution = {},
): Record<string, string> {
  const normalized = normalizeAttribution(attribution);
  return {
    utm_source: normalized.source,
    utm_medium: normalized.medium,
    utm_campaign: normalized.campaign,
    utm_content: normalized.content,
    ...(normalized.term ? { utm_term: normalized.term } : {}),
    ...(normalized.clickId ? { web_click_id: normalized.clickId } : {}),
  };
}

export const IOS_APP_STORE_WEBSITE_URL = getIosAppStoreUrl(
  DOWNLOAD_PAGE_ATTRIBUTION,
);
export const ANDROID_PLAY_STORE_WEBSITE_URL = getAndroidPlayStoreUrl(
  DOWNLOAD_PAGE_ATTRIBUTION,
);

export const IOS_APP_STORE_SMART_BANNER_URL = getIosAppStoreUrl(
  SMART_BANNER_ATTRIBUTION,
);
export const ANDROID_PLAY_STORE_SMART_BANNER_URL = getAndroidPlayStoreUrl(
  SMART_BANNER_ATTRIBUTION,
);
export const ANDROID_APP_INTENT_URL = getAndroidAppIntentUrl(
  DOWNLOAD_PAGE_ATTRIBUTION,
);
export const APP_DOWNLOAD_QR_TARGET_URL = `${APP_DOWNLOAD_URL}?source=qr`;

export function getAppDownloadPlatform(userAgent: string): AppDownloadPlatform {
  if (/Android/i.test(userAgent)) return "android";
  if (/iPhone|iPad|iPod/i.test(userAgent)) return "ios";
  return "other";
}

export function getWebsiteStoreUrl(
  platform: AppDownloadPlatform,
  attribution: AppDownloadAttribution = DOWNLOAD_PAGE_ATTRIBUTION,
): string {
  return platform === "android"
    ? getAndroidPlayStoreUrl(attribution)
    : getIosAppStoreUrl(attribution);
}

export function openIosAppStoreWithFallback(
  attribution: AppDownloadAttribution = DOWNLOAD_PAGE_ATTRIBUTION,
) {
  if (typeof window === "undefined") return;

  window.location.href = getIosNativeAppStoreUrl(attribution);
  window.setTimeout(() => {
    if (!document.hidden) {
      window.location.href = getIosAppStoreUrl(attribution);
    }
  }, 1200);
}

export function openMobileAppOrStore(
  platform: AppDownloadPlatform = typeof navigator === "undefined"
    ? "other"
    : getAppDownloadPlatform(navigator.userAgent),
  attribution: AppDownloadAttribution = DOWNLOAD_PAGE_ATTRIBUTION,
) {
  if (typeof window === "undefined") return;

  if (platform === "android") {
    window.location.href = getAndroidAppIntentUrl(attribution);
    return;
  }

  if (platform === "ios") {
    window.location.href = SOLMI_APP_DEEP_LINK_URL;
    window.setTimeout(() => {
      if (!document.hidden) {
        openIosAppStoreWithFallback(attribution);
      }
    }, 1000);
    return;
  }

  window.location.href = APP_DOWNLOAD_URL;
}
